// Where to go after sign-in. A page that needs a signed-in user (e.g. a supplier invitation)
// sends the visitor to /auth/login?returnTo=<path> or /auth/register?returnTo=<path>.
//
// Login uses the query value directly. Email signup leaves the browser: the activation link
// in the mail opens a new page without the query. So the register page remembers the path
// in localStorage, and the first sign-in in this browser takes it.

const KEY = "nb.returnTo";
// Most people click the activation mail within minutes. One day is enough, and it limits how
// long a forgotten path can apply to a later sign-in on a shared computer.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Accept only a same-origin app path. Rejects absolute URLs, "//host" and auth pages. */
export function safeReturnTo(value) {
  if (typeof value !== "string" || value.length === 0) return null;
  if (!value.startsWith("/")) return null;
  if (value.startsWith("//") || value.startsWith("/\\")) return null;
  if (value.includes("://")) return null;
  if (value === "/auth" || value.startsWith("/auth/")) return null;
  return value;
}

export function returnToFromUrl() {
  if (typeof window === "undefined") return null;
  return safeReturnTo(new URLSearchParams(window.location.search).get("returnTo"));
}

export function rememberReturnTo(path) {
  const safe = safeReturnTo(path);
  if (!safe) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path: safe, at: Date.now() }));
  } catch {
    /* storage blocked: login still works, the visitor lands on the home page */
  }
}

/** The path to open after sign-in, or null. Clears the remembered path. */
export function takeReturnTo() {
  const fromUrl = returnToFromUrl();
  let remembered = null;
  try {
    const raw = localStorage.getItem(KEY);
    localStorage.removeItem(KEY);
    if (raw) {
      const { path, at } = JSON.parse(raw);
      if (Date.now() - at < MAX_AGE_MS) remembered = safeReturnTo(path);
    }
  } catch {
    /* unreadable entry: ignore */
  }
  return fromUrl || remembered;
}
